/* eslint-disable react-hooks/exhaustive-deps */
import React, {useCallback, useContext, useEffect, useMemo} from 'react';
import {useSelector} from 'react-redux';
import {findKey, get} from 'lodash';

import {
  CLIENT_TO_CONTACT_REPORT_ENUM,
  // EXPIRING_REWARDS,
  EXPIRING_WELLNESS_CREDITS,
  NEW_CANDIDATE,
  NEXT_TASK,
  NO_ACTIVE_ORDER,
  NO_ORDER,
  NO_RX_ORDER,
  PREMIER_CANCELLATIONS,
  RECENTLY_INACTIVE,
} from '/enum/peopleToContact.enum';
import {
  // REPORT_CLIENT_TO_CONTACT_EXPIRING_REWARDS,
  REPORT_CLIENT_TO_CONTACT_EXPIRING_WELLNESS_CREDITS,
  REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE,
  REPORT_CLIENT_TO_CONTACT_NEXT_TASK,
  REPORT_CLIENT_TO_CONTACT_NO_ACTIVE_ORDER,
  REPORT_CLIENT_TO_CONTACT_NO_ORDER,
  REPORT_CLIENT_TO_CONTACT_NO_RX_ORDER,
  REPORT_CLIENT_TO_CONTACT_PREMIER_CANCELATION,
  REPORT_CLIENT_TO_CONTACT_RECENTLY_INACTIVE,
} from '/enum/records.enum';
import {FILTER_TYPES} from '/enum/tealium.enum';
import {useTranslation} from '/provider/TranslationProvider';
import {variablesFeatureFlag} from '/redux/selector/remoteConfig.selector';
import {shouldReleaseFeature} from '/util/featureRelease.util';
import {getDefaultRecordsSort} from '/util/sort.util';

import {NoRXOrder} from './NoRXOrder/NoRXOrder';
// import {ExpiringRewards} from './ExpiringRewards';
import {ExpiringWellnessCredits} from './ExpiringWellnessCredits';
import {NewCandidate} from './NewCandidate';
import {NextTask} from './NextTask';
import {NoActiveOrder} from './NoActiveOrder';
import {NoOrder} from './NoOrder';
import {generateOnTabPress} from './PeopleToContact.util';
import {PremierCancelation} from './PremierCancelation';
import {RecentlyInactive} from './RecentlyInactive';

import {RecordsSortContext} from '/context/RecordsSort/RecordsSort';

/**
 * @param {object} params - Parameters
 * @param {function} params.onShowProfile - function to show profile/profile card
 * @param {function} params.onShowReport - function to switch to the report screen
 * @param {function} params.fetchReport - function for loading report data
 *
 * @param {array} params.noOrder - report data array
 * @param {array} params.expiringRewards - report data array
 * @param {array} params.recentlyInactive - report data array
 * @param {array} params.premierCancellations - report data array
 * @param {array} params.expiringWellnessCredits - report data array
 * @param {array} params.nextTask - report data array
 * @param {array} params.noActiveOrder - report data array
 *
 * @param {boolean} params.hasNoOrder - variable showing the presence of INITIAL data for No Order Report
 * @param {boolean} params.hasExpiringRewards - variable showing the presence of INITIAL data for Expiring Rewards Report
 * @param {boolean} params.hasRecentlyInactive - variable showing the presence of INITIAL data for Recently Inactive Report
 * @param {boolean} params.hasPremierCancellations - variable showing the presence of INITIAL data for Premier Cancellations Report
 * @param {boolean} params.hasExpiringWellnessCredits - variable showing the presence of INITIAL data for Expiring Wellness Credits Report
 * @param {boolean} params.hasNextTask - variable showing the presence of INITIAL data for Next Task Report
 *
 * @param {boolean} params.isEmptyNoOrder - variable showing the presence of LOADED data for No Order Report
 * @param {boolean} params.isEmptyExpiringRewards - variable showing the presence of LOADED data for Expiring Rewards Report
 * @param {boolean} params.isEmptyRecentlyInactive - variable showing the presence of LOADED data for Recently Inactive Report
 * @param {boolean} params.isEmptyPremierCancellations - variable showing the presence of LOADED data for Premier Cancellations Report
 * @param {boolean} params.isEmptyExpiringWellnessCredits - variable showing the presence of LOADED data for Expiring Wellness Credits Report
 * @param {boolean} params.isEmptyNextTask - variable showing the presence of LOADED data for Next Task Report
 *
 * @param {object} params.noOrderMeta - metadata containing the total number of elements for No Order Report
 * @param {object} params.expiringRewardsMeta - metadata containing the total number of elements for Expiring Rewards Report
 * @param {object} params.recentlyInactiveMeta - metadata containing the total number of elements for Recently Inactive Report
 * @param {object} params.premierCancellationsMeta - metadata containing the total number of elements for Premier Cancellations Report
 * @param {object} params.expiringWellnessCreditsMeta - metadata containing the total number of elements for Expiring Wellness Credits Report
 * @param {object} params.nextTaskMeta - metadata containing the total number of elements for Next Task Report
 *
 * @param {boolean} params.noOrderIsLoading - is this report in the data loading state
 * @param {boolean} params.expiringRewardsIsLoading - is this report in the data loading state
 * @param {boolean} params.recentlyInactiveIsLoading - is this report in the data loading state
 * @param {boolean} params.premierCancellationsIsLoading - is this report in the data loading state
 * @param {boolean} params.expiringWellnessCreditsIsLoading - is this report in the data loading state
 * @param {boolean} params.nextTaskIsLoading - is this report in the data loading state
 *
 * @param {boolean} params.showFullReport - should we display the full report
 */
export const useClientToContact = ({
  featureFlag,
  onShowProfile,
  onShowReport,
  isEmptyNoOrder,
  isEmptyPremierCancellations,
  isEmptyRecentlyInactive,
  isEmptyExpiringWellnessCredits,
  // isEmptyExpiringRewards,
  isEmptyNextTask,
  isEmptyNoActiveOrder,
  isEmptyNoRXOrder,
  isEmptyNewCandidate,
  hasNoOrder,
  // hasExpiringRewards,
  hasRecentlyInactive,
  hasPremierCancellations,
  hasExpiringWellnessCredits,
  hasNextTask,
  hasNoActiveOrder,
  hasNoRXOrder,
  hasNewCandidate,
  parentScreenName,
  showFullReport,
  fetchReport,
  initialRouteName: LOADEDRouteName,
  noOrderIsLoading,
  // expiringRewardsIsLoading,
  recentlyInactiveIsLoading,
  premierCancellationsIsLoading,
  expiringWellnessCreditsIsLoading,
  nextTaskIsLoading,
  noActiveOrderIsLoading,
  noRXOrderIsLoading,
  newCandidateIsLoading,
  noOrderMeta,
  // expiringRewardsMeta,
  recentlyInactiveMeta,
  premierCancellationsMeta,
  expiringWellnessCreditsMeta,
  nextTaskMeta,
  noActiveOrderMeta,
  noRXOrderMeta,
  newCandidateMeta,
  isAlertsLoading,
  isClientJourneyLoading,
  lastPaidRank,
  highestRankDescription,
  userId,
}: any) => {
  const {ts} = useTranslation('component.partial.PeopleToContact');

  // const removeExpiringRewards = shouldReleaseFeature(
  //   get(featureFlag, 'ff_Remove_Expiring_Rewards', ''),
  // );

  const featureFlagPremier = useSelector(variablesFeatureFlag);
  const updatePremierPlus = shouldReleaseFeature(
    get(featureFlagPremier, 'ff_Premier_Plus', ''),
  );
  // Logic for the order title
  const updatedOrderType = updatePremierPlus
    ? 'Premier+ cancellation'
    : 'Premier cancelation';
  const getRecordsParams = (recordsName: any) => [
    {
      ...getDefaultRecordsSort({
        recordsName,
      })?.params,
    },
  ];

  /* istanbul ignore next */
  const tabComponent = useCallback(
    (Component: any, reportParams: any) => () =>
      (
        <Component
          reportParams={reportParams}
          onItemPress={onShowReport}
          onShowProfile={onShowProfile}
          showFullReport={showFullReport}
          parentScreenName={parentScreenName}
        />
      ),
    [],
  );

  // Creating an array of tabs. If there is no data in it, we should not display it
  const memoizedTabs = useMemo(() => {
    let tabs = [];

    if (!isEmptyNextTask && featureFlag.showClientJourney) {
      tabs.push({
        name: NEXT_TASK,
        component: tabComponent(
          NextTask,
          getRecordsParams(REPORT_CLIENT_TO_CONTACT_NEXT_TASK),
        ),
        label: ts('nextTask', `Next task (${nextTaskMeta?.page?.total})`),
        count: nextTaskMeta?.page?.total ?? 0,
        onTabPress: () => {
          setActiveRecordsName(REPORT_CLIENT_TO_CONTACT_NEXT_TASK);
          generateOnTabPress({
            filterType: FILTER_TYPES.nextTask,
            currentScreenName: parentScreenName,
            lastPaidRank: lastPaidRank,
            highestAchievedRank: highestRankDescription,
            coachId: userId,
          });
        },
      });
    }

    if (!isEmptyNoOrder) {
      tabs.push({
        name: NO_ORDER,
        component: tabComponent(
          NoOrder,
          getRecordsParams(REPORT_CLIENT_TO_CONTACT_NO_ORDER),
        ),
        label: ts('noOrder', `No order (${noOrderMeta?.page?.total})`),
        count: noOrderMeta?.page?.total ?? 0,
        onTabPress: () => {
          setActiveRecordsName(REPORT_CLIENT_TO_CONTACT_NO_ORDER);
          generateOnTabPress({
            filterType: FILTER_TYPES.noOrder,
            currentScreenName: parentScreenName,
            lastPaidRank: lastPaidRank,
            highestAchievedRank: highestRankDescription,
            coachId: userId,
          });
        },
      });
    }

    if (!isEmptyPremierCancellations) {
      tabs.push({
        name: PREMIER_CANCELLATIONS,
        component: tabComponent(
          PremierCancelation,
          getRecordsParams(REPORT_CLIENT_TO_CONTACT_PREMIER_CANCELATION),
        ),
        label: ts(
          'premierCancellations',
          `${updatedOrderType} (${premierCancellationsMeta?.page?.total})`,
        ),
        count: premierCancellationsMeta?.page?.total ?? 0,
        onTabPress: () => {
          setActiveRecordsName(REPORT_CLIENT_TO_CONTACT_PREMIER_CANCELATION);
          generateOnTabPress({
            filterType: FILTER_TYPES.premierCancellation,
            currentScreenName: parentScreenName,
            lastPaidRank: lastPaidRank,
            highestAchievedRank: highestRankDescription,
            coachId: userId,
          });
        },
      });
    }

    if (!isEmptyRecentlyInactive) {
      tabs.push({
        name: RECENTLY_INACTIVE,
        component: tabComponent(
          RecentlyInactive,
          getRecordsParams(REPORT_CLIENT_TO_CONTACT_RECENTLY_INACTIVE),
        ),
        label: ts(
          'recentlyInactive',
          `Recently inactive (${recentlyInactiveMeta?.page?.total})`,
        ),
        count: recentlyInactiveMeta?.page?.total ?? 0,
        onTabPress: () => {
          setActiveRecordsName(REPORT_CLIENT_TO_CONTACT_RECENTLY_INACTIVE);
          generateOnTabPress({
            filterType: FILTER_TYPES.recentlyInactive,
            currentScreenName: parentScreenName,
            lastPaidRank: lastPaidRank,
            highestAchievedRank: highestRankDescription,
            coachId: userId,
          });
        },
      });
    }

    if (!isEmptyExpiringWellnessCredits) {
      tabs.push({
        name: EXPIRING_WELLNESS_CREDITS,
        component: tabComponent(
          ExpiringWellnessCredits,
          getRecordsParams(REPORT_CLIENT_TO_CONTACT_EXPIRING_WELLNESS_CREDITS),
        ),
        label: ts(
          'expiringWellnessCredits',
          `Expiring wellness credits (${expiringWellnessCreditsMeta?.page?.total})`,
        ),
        count: expiringWellnessCreditsMeta?.page?.total ?? 0,
        onTabPress: () => {
          setActiveRecordsName(
            REPORT_CLIENT_TO_CONTACT_EXPIRING_WELLNESS_CREDITS,
          );
          generateOnTabPress({
            filterType: FILTER_TYPES.expiringWellnessCredits,
            currentScreenName: parentScreenName,
            lastPaidRank: lastPaidRank,
            highestAchievedRank: highestRankDescription,
            coachId: userId,
          });
        },
      });
    }
    if (featureFlag.showActive && !isEmptyNoActiveOrder) {
      tabs.push({
        name: NO_ACTIVE_ORDER,
        component: tabComponent(
          NoActiveOrder,
          getRecordsParams(REPORT_CLIENT_TO_CONTACT_NO_ACTIVE_ORDER),
        ),
        label: ts(
          'noActiveOrder',
          `No ACTIVE order (${noActiveOrderMeta?.page?.total})`,
        ),
        count: noActiveOrderMeta?.page?.total ?? 0,
        onTabPress: () => {
          setActiveRecordsName(REPORT_CLIENT_TO_CONTACT_NO_ACTIVE_ORDER);
          generateOnTabPress({
            filterType: FILTER_TYPES.noActiveOrder,
            currentScreenName: parentScreenName,
            lastPaidRank: lastPaidRank,
            highestAchievedRank: highestRankDescription,
            coachId: userId,
          });
        },
      });
    }
    if (featureFlag.show_P_T_C_NewCandidate && !isEmptyNewCandidate) {
      tabs.push({
        name: NEW_CANDIDATE,
        component: tabComponent(
          NewCandidate,
          getRecordsParams(REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE),
        ),
        label: ts(
          'newCandidate',
          `New candidates (${newCandidateMeta?.page?.total})`,
        ),
        count: newCandidateMeta?.page?.total ?? 0,

        onTabPress: () => {
          setActiveRecordsName(REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE);
          generateOnTabPress({
            filterType: FILTER_TYPES.candidate,
            currentScreenName: parentScreenName,
            lastPaidRank: lastPaidRank,
            highestAchievedRank: highestRankDescription,
            coachId: userId,
          });
        },
      });
    }
    if (featureFlag.showRX && !isEmptyNoRXOrder) {
      tabs.push({
        name: NO_RX_ORDER,
        component: tabComponent(
          NoRXOrder,
          getRecordsParams(REPORT_CLIENT_TO_CONTACT_NO_RX_ORDER),
        ),
        label: ts('noRXOrder', `No RX order (${noRXOrderMeta?.page?.total})`),
        count: noRXOrderMeta?.page?.total ?? 0,

        onTabPress: () => {
          setActiveRecordsName(REPORT_CLIENT_TO_CONTACT_NO_RX_ORDER);
          generateOnTabPress({
            filterType: FILTER_TYPES.noRXOrder,
            currentScreenName: parentScreenName,
            lastPaidRank: lastPaidRank,
            highestAchievedRank: highestRankDescription,
            coachId: userId,
          });
        },
      });
    }

    return tabs;
  }, [
    isEmptyNoOrder,
    isEmptyPremierCancellations,
    isEmptyRecentlyInactive,
    isEmptyExpiringWellnessCredits,
    // isEmptyExpiringRewards,
    isEmptyNextTask,
    isClientJourneyLoading,
    isEmptyNoActiveOrder,
    isEmptyNoRXOrder,
    newCandidateMeta,
  ]);

  useEffect(() => {
    if (!hasNoOrder && !noOrderIsLoading) {
      fetchReport({
        type: NO_ORDER,
        reportName: REPORT_CLIENT_TO_CONTACT_NO_ORDER,
        reportParams: getRecordsParams(REPORT_CLIENT_TO_CONTACT_NO_ORDER),
      });
    }

    if (!hasExpiringWellnessCredits && !expiringWellnessCreditsIsLoading) {
      fetchReport({
        type: EXPIRING_WELLNESS_CREDITS,
        reportName: REPORT_CLIENT_TO_CONTACT_EXPIRING_WELLNESS_CREDITS,
        reportParams: getRecordsParams(
          REPORT_CLIENT_TO_CONTACT_EXPIRING_WELLNESS_CREDITS,
        ),
      });
    }

    if (!hasRecentlyInactive && !recentlyInactiveIsLoading) {
      fetchReport({
        type: RECENTLY_INACTIVE,
        reportName: REPORT_CLIENT_TO_CONTACT_RECENTLY_INACTIVE,
        reportParams: getRecordsParams(
          REPORT_CLIENT_TO_CONTACT_RECENTLY_INACTIVE,
        ),
      });
    }
    if (!hasPremierCancellations && !premierCancellationsIsLoading) {
      fetchReport({
        type: PREMIER_CANCELLATIONS,
        reportName: REPORT_CLIENT_TO_CONTACT_PREMIER_CANCELATION,
        reportParams: getRecordsParams(
          REPORT_CLIENT_TO_CONTACT_PREMIER_CANCELATION,
        ),
      });
    }
    if (!hasNextTask && featureFlag.showClientJourney && !nextTaskIsLoading) {
      fetchReport({
        type: NEXT_TASK,
        reportName: REPORT_CLIENT_TO_CONTACT_NEXT_TASK,
        reportParams: getRecordsParams(REPORT_CLIENT_TO_CONTACT_NEXT_TASK),
      });
    }
    if (
      !hasNoActiveOrder &&
      featureFlag.showActive &&
      !noActiveOrderIsLoading
    ) {
      fetchReport({
        type: NO_ACTIVE_ORDER,
        reportName: REPORT_CLIENT_TO_CONTACT_NO_ACTIVE_ORDER,
        reportParams: getRecordsParams(
          REPORT_CLIENT_TO_CONTACT_NO_ACTIVE_ORDER,
        ),
      });
    }
    if (!hasNewCandidate && !newCandidateIsLoading) {
      fetchReport({
        type: NEW_CANDIDATE,
        reportName: REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE,
        reportParams: getRecordsParams(REPORT_CLIENT_TO_CONTACT_NEW_CANDIDATE),
      });
    }
    if (!hasNoRXOrder && featureFlag.showRX && !noRXOrderIsLoading) {
      fetchReport({
        type: NO_RX_ORDER,
        reportName: REPORT_CLIENT_TO_CONTACT_NO_RX_ORDER,
        reportParams: getRecordsParams(REPORT_CLIENT_TO_CONTACT_NO_RX_ORDER),
      });
    }
  }, [
    hasNoOrder,
    hasPremierCancellations,
    hasRecentlyInactive,
    hasExpiringWellnessCredits,
    // hasExpiringRewards,
    hasNextTask,
    hasNoActiveOrder,
    hasNoRXOrder,
    hasNewCandidate,
    generateOnTabPress,
    isAlertsLoading,
  ]);

  const currentLOADEDRouteName = useMemo(() => {
    const oldDefaultRouteName = hasPremierCancellations
      ? PREMIER_CANCELLATIONS
      : memoizedTabs[0]?.name;
    const newDefaultRouteName = hasNextTask ? NEXT_TASK : memoizedTabs[0]?.name;
    const defaultRouteName = featureFlag.showClientJourney
      ? newDefaultRouteName
      : oldDefaultRouteName;
    return LOADEDRouteName || defaultRouteName;
  }, [memoizedTabs, hasPremierCancellations]);

  const {setActiveRecordsName, resetSortParams} =
    useContext(RecordsSortContext);

  useEffect(() => {
    if (currentLOADEDRouteName) {
      const recordsName = findKey(
        CLIENT_TO_CONTACT_REPORT_ENUM,
        (val: string) => val === currentLOADEDRouteName,
      );
      if (recordsName) {
        setActiveRecordsName(recordsName);
        resetSortParams({recordsName});
      }
    }
  }, [currentLOADEDRouteName]);

  return {
    tabs: memoizedTabs,
    currentLOADEDRouteName,
  };
};
